import { HelperService } from './services/helper.service'
import { BrowserModule } from '@angular/platform-browser'
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core'
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http'
import { RouterModule, PreloadAllModules } from '@angular/router'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { ROUTES } from './app.routes'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { NgxMaskModule } from 'ngx-mask'
import { TooltipModule } from 'ng2-tooltip-directive'
import { HashLocationStrategy, LocationStrategy } from '@angular/common'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material'
import { MAT_DATE_LOCALE } from '@angular/material'

// Libs
import 'boxicons'
import { NgxSpinnerModule } from 'ngx-spinner'
import { DataTablesModule } from 'angular-datatables'
import { InputTrimModule } from 'ng2-trim-directive'
// import FixedColumns from 'datatables.net-fixedcolumns'
import { NgSelectModule } from '@ng-select/ng-select'
import { AgmCoreModule } from '@agm/core'
import { ChartsModule } from 'ng2-charts'
import { NgImageSliderModule } from 'ng-image-slider'

// pipes
import { FormatRisk } from './pipes/format-risk.pipe'
import { FormatDistance } from './pipes/format-distance.pipe'
import { FormatRoleAdmins } from './pipes/format-roles.pipe'
import { RiskAssessment } from './pipes/risk-assessment.pipe'
import { CapitalizeFirstPipe } from './pipes/capitalize-first.pipe'
import { TreeNotFoundReasonPipe } from './pipes/tree-notfound-reason.pipe'
import { ExcludeRejectedPipe } from './pipes/filter-handler-operation.pipe'

// middlewares
import { AuthGuard } from './guards/auth.guard'
import { HttpInterceptorService } from './services/http.interceptor.service'

// services
import { AuthenticationService } from './services/authentication.service'
import { AWSService } from './services/aws.service'
import { HeaderService } from './services/header.service'
import { UserService } from './services/user.service'
import { RealtimeLocationService } from './services/realtimeLocation.service'
import { SpeciesService } from './services/species.service'
import { NotificationService } from './services/notification.service'
import { CompanyService } from './services/company.service'
import { BotanicService } from './services/botanic.service'
import { SchedulingService } from './services/scheduling.service'
import { ExcelService } from './services/excel.service'
import { EvaluationService } from './services/evaluation.service'
import { HandlerOperationService } from './services/handler.operation.service'
import { DemandService } from './services/demand.service'
import { OperationServiceService } from './services/operation.service.service'
import { DashboardService } from './services/dashboard.service'
import { TreeService } from './services/tree.service'
import { PlantingService } from './services/planting.service'
import { RegionsService } from './services/regions.service'

// components
import { NewService } from './components/protected/scheduler/newService/newService.component'
import { GeneralFeatures } from './components/protected/scheduler-planting/general-features/general-features.component'
import { AppComponent } from './app.component'
import { LoadingComponent } from './components/shared/loading/loading.component'
import { LoginComponent } from './components/login/login.component'
import { RememberPasswordComponent } from './components/remember-password/remember-password.component'
import { ResetPasswordComponent } from './components/reset-password/reset-password.component'
import { ValidationReportComponent } from './components/validation-report/validation-report.component'
import { PanelComponent } from './components/protected/panel/panel.component'
import { TermsComponent } from './components/protected/terms/terms.component'
import { DataCsvComponent } from './components/protected/data-csv/data-csv.component'
import { DataCsvInventoryComponent } from './components/protected/data-csv-inventory/data-csv-inventory.component'

import { AdminComponent } from './components/protected/admin/admin.component'
import { CompanyComponent } from './components/protected/company/company.component'
import { SnackbarComponent } from './components/shared/snackbar/snackbar.component'
import { HeaderComponent } from './components/shared/header/header.component'
import { BotanicComponent } from './components/protected/botanic/botanic.component'
import { ModalDeleteComponent } from './components/modal/delete/modal-delete.component'
import { ModalDeleteDemandComponent } from './components/modal/delete-demand/modal-delete-demand.component'
import { ModalReactivateDemandComponent } from './components/modal/reactivate-demand/modal-reactivate-demand.component'
import { ModalDialogComponent } from './components/modal/dialog/modal-dialog.component'
import { ModalFormComponent } from './components/modal/form/modal-form.component'
import { ModalCompanyComponent } from './components/modal/company/modal-company.component'
import { UserDetailComponent } from './components/protected/user-detail/user-detail.component'
import { CompanyAdminComponent } from './components/protected/company-admin/company-admin.component'
import { MapView } from './components/map-view/map-view.component'

import { RedirectComponent } from './components/protected/redirect/redirect.component'
import { BreadcrumbComponent } from './components/shared/breadcrumb/breadcrumb.component'
import { HeaderDetailComponent } from './components/shared/header-detail/header-detail.component'
import { SchedulerComponent } from './components/protected/scheduler/scheduler.component'
import { SpeciesComponent } from './components/protected/species/species.component'
import { ScheduleComponent } from './components/protected/scheduler/schedule/schedule.component'
import { StepsComponent } from './components/protected/scheduler/steps/steps.component'
import { EvaluationComponent } from './components/protected/evaluation/evaluation.component'
import { HandlerOperationComponent } from './components/protected/handler-operation/handler-operation.component'
import { ManagementConfigurationComponent } from './components/protected/management-configuration/management-configuration.component'
import { DemandsHistoryComponent } from './components/protected/demands-history/demands-history.component'
import { OperationServiceComponent } from './components/protected/operation-service/operation-service.component'
import { OperatorSchedulingComponent } from './components/protected/operation-service/operator-scheduling/operator-scheduling.component'
import { OperationServiceHistoryComponent } from './components/protected/operation-service-history/operation-service-history.component'
import { OperationServiceReportArborealComponent } from './components/protected/operation-service-report-arboreal/operation-service-report-arboreal.component'
import { DashboardComponent } from './components/protected/dashboard/dashboard.component'
import { PublicPanelComponent } from './components/protected/central-control/public-panel/public-panel.component'
import { TermsService } from './services/terms.service'
import { OperationScheduleComponent } from './components/protected/operation-service/operation-schedule/operation-schedule.component'
import { InsertDateComponent } from './components/modal/insert-date/insert-date.component'
import { RealtimeLocationComponent } from './components/protected/realtime-location/realtime-location.component'
import { SchedulerInventoryComponent } from './components/protected/scheduler-inventory/scheduler-inventory.component'
import { ScheduleInventoryComponent } from './components/protected/scheduler-inventory/schedule-inventory/schedule-inventory.component'

import { SchedulerPlantingComponent } from './components/protected/scheduler-planting/scheduler-planting.component'
import { SchedulePlantingComponent } from './components/protected/scheduler-planting/schedule-planting/schedule-planting.component'
import { HistoryPlantingComponent } from './components/protected/history-planting/history-planting.component'
import { InventoryService } from './services/inventory.service'
import { HistoryInventoryComponent } from './components/protected/history-inventory/history-inventory.component'
import { LoaderBackgroundComponent } from './components/loader/loader.component'
import { WebsocketService } from './services/websocket.service'
import { CompanyServiceProviderComponent } from './components/protected/company-service-provider/company-service-provider.component'
import { CompanyServiceProviderService } from './services/company-service-provider'
import { InputNumber } from './components/shared/input-number/input-number.component'
import { Textarea } from './components/shared/textarea/textarea.component'
import { PrivacyComponent } from './components/privacy/privacy.component'
import { MapScheduleView } from './components/map-schedule-view/map-schedule-view.component'
import { CommunicationAppraisal } from './components/protected/communication-appraisal/communicationAppraisal.component'
import { RegionsComponent } from './components/protected/regions/regions.component'
import { RegionComponent } from './components/protected/regions/region/region.component'
import { ImportKmlService } from './services/importKml.service'
import { InventoryReportArborealComponent } from './components/protected/inventory-report-arboreal/inventory-report-arboreal.component'
import { PlantingReportArborealComponent } from './components/protected/planting-report-arboreal/planting-report-arboreal.component'
import { PlantingOpportunityReportArborealComponent } from './components/protected/plantingOpportunity-report-arboreal/plantingOpportunity-report-arboreal.component'
import { OperationServiceScheduleReportArborealComponent } from './components/protected/operation-service-schedule-report-arboreal/operation-service-schedule-report-arboreal.component'
import { InputSearch } from './components/shared/input-search/input-search.component'
import { RequestersComponent } from './components/protected/requesters/requesters.component'
import { RequesterComponent } from './components/protected/requesters/requester/requester.component'
import { SelectLengthTable } from './components/shared/select-length-table/select-length-table.component'
import { PreScheduleInventoryComponent } from './components/protected/scheduler-inventory/pre-schedule/pre-scheduling-inventory.component'
import { PreScheduleOperationServiceComponent } from './components/protected/operation-service/pre-scheduling/operation-service-pre-scheduling.component'
import { DemandsForAttachComponent } from './components/protected/demands-history/demands-attach/demands-attach.component'
import { SelectHandleOperation } from './components/shared/select-handler-operations/select-handler-operations.component'
import { SectionsComponent } from './components/protected/handler-operation/sections/sections.component'
import { SectionComponent } from './components/protected/handler-operation/sections/section/section.component'
import { AppraisalReportComponent } from './components/protected/evaluation-reports/appraisal/appraisal-report.component'
import { HeaderReportComponent } from './components/shared/report-components/header-report/header-report.component'
import { GeneralInformationComponent } from './components/shared/report-components/general-informations/general-informations.component'
import { LocationComponent } from './components/shared/report-components/location/location.component'
import { TreeInformationsComponent } from './components/shared/report-components/tree-informations/tree-informations.component'
import { EvaluationResultComponent } from './components/shared/report-components/evaluation-result/evaluation-result.component'
import { ManagementRecommendationComponent } from './components/shared/report-components/management-recommendation/management-recommendation.component'
import { EvaluationRiskComponent } from './components/shared/report-components/evaluation-risk/evaluation-risk.component'
import { LegalFundamentationsComponent } from './components/shared/report-components/legal-fundamentations/legal-fundamentations.component'
import { SelectSectionsComponent } from './components/shared/select-sections/select-sections.component'
import { AnswersComponent } from './components/shared/report-components/answers/answers.component'
import { PhotoRecordsComponent } from './components/shared/report-components/photo-records/photo-records.component'
import { PaginationComponent } from './components/shared/report-components/pagination/pagination.component'
import { FooterComponent } from './components/shared/report-components/footer-report/footer-report.component'
import { LoadingPageComponent } from './components/shared/report-components/loading-page/loading-page.component'
import { DetailsNearDemandComponent } from './components/protected/scheduler/schedule/details-near-demand/details-near-demand'
import { CentralControlService } from './services/central-control.service'
import { ExportService } from './services/export.service'
import { SelectCompanyProvider } from './components/shared/select-company-provider/select-company-provider.component'
import { TableSettings } from './components/shared/table-settings/table-settings.components'
import { GOOGLE_API_KEY } from './app.api'
import { MetabaseService } from './services/metabase.service'

@NgModule({
	declarations: [
		AppComponent,
		SnackbarComponent,
		LoadingComponent,
		LoginComponent,
		LoaderBackgroundComponent,
		PanelComponent,
		TermsComponent,
		DataCsvComponent,
		DataCsvInventoryComponent,
		RealtimeLocationComponent,
		AdminComponent,
		CompanyComponent,
		RequestersComponent,
		RequesterComponent,
		ModalDeleteComponent,
		ModalDeleteDemandComponent,
		ModalReactivateDemandComponent,
		ModalDialogComponent,
		ModalFormComponent,
		ModalCompanyComponent,
		RememberPasswordComponent,
		HeaderComponent,
		InputSearch,
		ResetPasswordComponent,
		ValidationReportComponent,
		HistoryInventoryComponent,
		UserDetailComponent,
		BotanicComponent,
		CompanyAdminComponent,
		CompanyServiceProviderComponent,
		RedirectComponent,
		BreadcrumbComponent,
		HeaderDetailComponent,
		SchedulerComponent,
		SchedulerInventoryComponent,
		ScheduleInventoryComponent,
		ScheduleComponent,
		StepsComponent,
		SpeciesComponent,
		EvaluationComponent,
		HandlerOperationComponent,
		ManagementConfigurationComponent,
		DemandsHistoryComponent,
		PreScheduleInventoryComponent,
		PreScheduleOperationServiceComponent,
		CommunicationAppraisal,
		OperationServiceComponent,
		OperatorSchedulingComponent,
		OperationServiceHistoryComponent,
		OperationServiceReportArborealComponent,
		DashboardComponent,
		PublicPanelComponent,
		OperationScheduleComponent,
		InsertDateComponent,
		FormatRisk,
		CapitalizeFirstPipe,
		ExcludeRejectedPipe,
		TreeNotFoundReasonPipe,
		RiskAssessment,
		FormatDistance,
		FormatRoleAdmins,
		SchedulerPlantingComponent,
		SchedulePlantingComponent,
		HistoryPlantingComponent,
		GeneralFeatures,
		MapView,
		MapScheduleView,
		NewService,
		InputNumber,
		SelectHandleOperation,
		Textarea,
		PrivacyComponent,
		RegionsComponent,
		RegionComponent,
		InventoryReportArborealComponent,
		PlantingReportArborealComponent,
		PlantingOpportunityReportArborealComponent,
		OperationServiceScheduleReportArborealComponent,
		SelectLengthTable,
		DemandsForAttachComponent,
		SectionsComponent,
		SectionComponent,
		AppraisalReportComponent,
		HeaderReportComponent,
		GeneralInformationComponent,
		LocationComponent,
		TreeInformationsComponent,
		EvaluationResultComponent,
		ManagementRecommendationComponent,
		EvaluationRiskComponent,
		LegalFundamentationsComponent,
		SelectSectionsComponent,
		AnswersComponent,
		PhotoRecordsComponent,
		PaginationComponent,
		FooterComponent,
		LoadingPageComponent,
		DetailsNearDemandComponent,
		SelectCompanyProvider,
		TableSettings
	],
	entryComponents: [InsertDateComponent, ModalDeleteComponent, ModalDeleteDemandComponent, ModalReactivateDemandComponent],
	imports: [
		InputTrimModule,
		BrowserModule,
		BrowserAnimationsModule,
		HttpClientModule,
		FormsModule,
		ReactiveFormsModule,
		RouterModule.forRoot(ROUTES, { preloadingStrategy: PreloadAllModules }),
		DataTablesModule,
		NgSelectModule,
		NgxSpinnerModule,
		NgxMaskModule.forRoot(),
		TooltipModule,
		AgmCoreModule.forRoot({
			apiKey: GOOGLE_API_KEY
		}),
		ChartsModule,
		NgImageSliderModule,
		MatDatepickerModule,
		MatNativeDateModule
	],
	providers: [
		HelperService,
		WebsocketService,
		MatDatepickerModule,
		AuthGuard,
		AuthenticationService,
		AWSService,
		InventoryService,
		RealtimeLocationService,
		UserService,
		TermsService,
		NotificationService,
		CompanyService,
		CompanyServiceProviderService,
		HeaderService,
		PlantingService,
		BotanicService,
		ExcelService,
		ExportService,
		SchedulingService,
		SpeciesService,
		EvaluationService,
		HandlerOperationService,
		RegionsService,
		ImportKmlService,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpInterceptorService,
			multi: true
		},
		ManagementConfigurationComponent,
		DemandService,
		CentralControlService,
		OperationServiceService,
		MetabaseService,
		DashboardService,
		TreeService,
		{ provide: LocationStrategy, useClass: HashLocationStrategy },
		{ provide: MAT_DATE_LOCALE, useValue: 'pt-BR' }
	],
	bootstrap: [AppComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
